import { foregroundGet } from "@/api/base/private/backgroundClient";
import { lookupAttempt, rememberExam } from "./attemptIndex";
import { findAttemptById } from "./attemptsQuery";

export interface SessionTarget {
  assignmentId: number;
  assignmentName?: string | null;
  studentId: number;
  studentName?: string | null;
  attemptId?: number | null;
  isRunning: boolean;
}

export type ResolveResult =
  | { kind: "resolved"; target: SessionTarget }
  | { kind: "choice"; targets: SessionTarget[] }
  | { kind: "not-found" }
  | { kind: "error" };

export type LookupKind = "attempt" | "person";

const STATUS_FRESH_MS = 5 * 60 * 1000;
const PERSON_LIMIT = 5;

interface AssignmentLite {
  id: number;
  name?: string | null;
  status?: string | null;
}

interface StudentLite {
  id: number;
  user?: { firstname?: string | null; lastname?: string | null } | null;
}

const unwrapList = <T,>(payload: unknown): T[] => {
  const entities = (payload as { entities?: unknown } | null)?.entities;
  if (Array.isArray(entities)) return entities as T[];

  const data = (entities as { data?: unknown } | null)?.data;
  if (Array.isArray(data)) return data as T[];

  return [];
};

const fullName = (student: StudentLite): string =>
  [student.user?.firstname, student.user?.lastname].filter(Boolean).join(" ").trim();

export function classifyLookupValue(rawValue: string): LookupKind {
  const value = decodeURIComponent(rawValue).trim();

  if (value.includes("@")) return "person";

  const digitsOnly = /^\d+$/.test(value);
  if (digitsOnly && value.length <= 9) return "attempt";

  return "person";
}

async function fetchAssignment(assignmentId: number): Promise<AssignmentLite | null> {
  const { data } = await foregroundGet(`/assignments/${assignmentId}`, {
    headers: { "X-Requested-Fields": "id,name,status" },
  });

  const entity = (data as { entity?: AssignmentLite } | null)?.entity;

  return entity ?? null;
}

async function resolveDirectly(attemptId: number): Promise<ResolveResult | null> {
  const found = await findAttemptById(attemptId);

  if (found.status === "unsupported") return null;
  if (found.status === "absent") return { kind: "not-found" };

  const assignmentId = Number(found.attempt.assignment_id);
  const studentId = Number(found.attempt.student_id);

  if (!Number.isFinite(assignmentId) || !Number.isFinite(studentId)) return null;

  const inlined = found.attempt.assignment;

  const assignment =
    inlined && (inlined.name != null || inlined.status != null)
      ? { id: assignmentId, name: inlined.name ?? null, status: inlined.status ?? null }
      : await fetchAssignment(assignmentId).catch(() => null);

  void rememberExam(
    assignmentId,
    assignment?.name ?? null,
    assignment?.status ?? null,
    [{ id: attemptId, student_id: studentId }],
    true
  );

  return {
    kind: "resolved",
    target: {
      assignmentId,
      assignmentName: assignment?.name ?? null,
      studentId,
      attemptId,
      isRunning: assignment?.status === "process",
    },
  };
}

async function fetchStudents(query: string): Promise<StudentLite[]> {
  const { data } = await foregroundGet("/students", {
    params: { query, limit: 10, per_page: 10 },
    headers: { "X-Requested-Fields": "id,user_id,user:id,user:firstname,user:lastname" },
  });

  return unwrapList<StudentLite>(data);
}

async function fetchStudentAssignments(studentId: number): Promise<AssignmentLite[]> {
  const { data } = await foregroundGet("/assignments", {
    params: { student_id: studentId, proctoring: 1, limit: 10, per_page: 10 },
    headers: { "X-Requested-Fields": "id,name,status" },
  });

  return unwrapList<AssignmentLite>(data);
}

const RESOLVE_CACHE = new Map<number, { at: number; result: ResolveResult }>();
const RESOLVE_TTL_MS = 120_000;

export function dropResolveCache(): void {
  RESOLVE_CACHE.clear();
}

export async function resolveByAttemptId(attemptId: number): Promise<ResolveResult> {
  const cached = RESOLVE_CACHE.get(attemptId);
  if (cached && Date.now() - cached.at < RESOLVE_TTL_MS) return cached.result;

  const known = await lookupAttempt(attemptId).catch(() => null);

  if (known) {
    const result: ResolveResult = {
      kind: "resolved",
      target: {
        assignmentId: known.assignmentId,
        assignmentName: known.assignmentName,
        studentId: known.studentId,
        studentName: known.studentName,
        attemptId,
        isRunning:
          known.status === "process" && Date.now() - known.statusAt < STATUS_FRESH_MS,
      },
    };

    RESOLVE_CACHE.set(attemptId, { at: Date.now(), result });
    return result;
  }

  const direct = await resolveDirectly(attemptId).catch(() => null);

  if (!direct) return { kind: "error" };

  RESOLVE_CACHE.set(attemptId, { at: Date.now(), result: direct });
  return direct;
}

export async function resolveByPerson(rawValue: string): Promise<ResolveResult> {
  const query = decodeURIComponent(rawValue).trim();

  try {
    const students = await fetchStudents(query);
    if (students.length === 0) return { kind: "not-found" };

    const targets: SessionTarget[] = [];

    for (const student of students.slice(0, PERSON_LIMIT)) {
      const assignments = await fetchStudentAssignments(student.id);

      for (const assignment of assignments) {
        if (assignment.status !== "process" && assignment.status !== "completed") continue;

        targets.push({
          assignmentId: assignment.id,
          assignmentName: assignment.name ?? null,
          studentId: student.id,
          studentName: fullName(student) || null,
          isRunning: assignment.status === "process",
        });
      }
    }

    if (targets.length === 0) return { kind: "not-found" };

    const running = targets.filter((target) => target.isRunning);
    if (running.length === 1) return { kind: "resolved", target: running[0] };
    if (targets.length === 1) return { kind: "resolved", target: targets[0] };

    return { kind: "choice", targets: running.length > 0 ? running : targets };
  } catch {
    return { kind: "error" };
  }
}

export function buildSessionUrl(
  target: SessionTarget,
  options: { live?: boolean } = {}
): string {
  const params = new URLSearchParams({ student: String(target.studentId) });

  if (target.attemptId != null) {
    params.set("attempt", String(target.attemptId));
  }

  const watchLive = options.live ?? target.isRunning;
  params.set("tab", watchLive ? "proctorings" : "students");

  return `/assignments/${target.assignmentId}?${params.toString()}`;
}
